/**
 * AuthContext — Step 10. One /auth/me probe at mount; 401 is an ANSWER (show
 * the login page), not an error — which is why this is the one sanctioned bare
 * fetch outside api.ts (digitaladmin's exact exemption, for the exact reason:
 * apiFetch would redirect on 401 and loop the login page into itself).
 */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

export interface Me {
  user: {
    id: string;
    email: string;
    name: string | null;
    kind: string;
    /** The roles authorisation is using — the IMPERSONATED one when active. */
    roles: string[];
    /** Who you actually are. Step 99: impersonation borrows a role, never an
     *  identity, so this never changes and the audit trail stays honest. */
    realRoles?: string[];
  };
  permissions: Record<string, { view: boolean; edit: boolean; admin: boolean }>;
  /** Step 99 — computed from the REAL identity, so the way out of
   *  impersonation is reachable however low the role being tested. */
  canImpersonate?: boolean;
  impersonating?: { roleKey: string; expiresAt: number } | null;
  /** Step 165 — true for an admin on an install whose setup wizard has not
   *  been completed. `RequireSignIn` sends them to `/setup`. */
  setupRequired?: boolean;
}

interface AuthState {
  me: Me | null;
  loading: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthCtx = createContext<AuthState>({
  me: null,
  loading: true,
  refresh: async () => {},
  signOut: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      // Bare fetch on purpose: 401 here means "signed out", not "redirect me".
      const res = await fetch('/auth/me', { credentials: 'same-origin' });
      setMe(res.ok ? await res.json() : null);
    } catch {
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    // Bare fetch on purpose: auth-domain plumbing. `apiSend`'s 401 handling
    // would redirect to the login page mid-sign-out, which is the thing we are
    // already doing, more slowly and with a flash of the wrong screen.
    await fetch('/auth/logout', { method: 'POST', credentials: 'same-origin' }).catch(() => {});
    setMe(null);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return <AuthCtx.Provider value={{ me, loading, refresh, signOut }}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  return useContext(AuthCtx);
}

export function useCanView() {
  const { me } = useAuth();
  return (resourceKey: string) => !!me?.permissions[resourceKey]?.view;
}
